import type { FinancePayableOverviewFilters, FinanceReceivableOverviewFilters } from "../types";

/** 看请款记录的权限码，和后端、权限注册表一致；菜单和路由里写的是同一个码。 */
export const FINANCE_PAYMENT_REQUEST_ACCESS_PERMISSION = "finance.payment_request:access";

/** 发起请款的权限码：应付清单上的「请款」按钮、请款表单、上传附件、提交都要它。 */
export const FINANCE_PAYMENT_REQUEST_CREATE_PERMISSION = "finance.payment_request:create";

/** 登记已付的权限码：请款详情里的「登记已付」「作废这次登记」、上传付款回单都要它，有它才看得到完整收款账号。 */
export const FINANCE_PAYMENT_REQUEST_RECORD_PAYMENT_PERMISSION = "finance.payment_request:record_payment";

export const financePaymentRequestQueryKey = ["finance", "payment-request"] as const;

/** 应付清单页查询的 queryKey 根：搜团、按团编码找团、应付汇总都挂在它下面。 */
export const financePayableQueryKey = ["finance", "payable"] as const;

/** 应付汇总的 queryKey 前缀：按它刷新会刷到所有团的应付汇总和预付款汇总（撤回时不知道是哪个团，用它）。 */
export const financePayableSummaryQueryKey = [...financePayableQueryKey, "settlement-summary"] as const;

/**
 * 一个团的应付汇总 queryKey；这个团的预付款汇总挂在它下面，按它刷新时两个一起重新读取。
 * 读应付汇总、读预付款汇总、提交请款后刷新都从这里取，不在别处另拼。
 */
export function financePayableItinerarySummaryQueryKey(itineraryId: string | null) {
  return [...financePayableSummaryQueryKey, itineraryId] as const;
}

/** 请款记录每页条数。 */
export const FINANCE_PAYMENT_REQUEST_PAGE_SIZE = 20;

/** 单个附件上限，与后端一致。 */
export const FINANCE_PAYMENT_REQUEST_MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;

/** 选文件时只列 PDF、PNG、JPEG。 */
export const FINANCE_PAYMENT_REQUEST_ATTACHMENT_ACCEPT = ".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg";

/** 导出应收列表的权限码：应收列表上的「导出 Excel」按钮要它，后端导出接口校验同一个码。 */
export const FINANCE_RECEIVABLE_LIST_EXPORT_PERMISSION = "finance.receivable_list:export";

/** 导出应付列表的权限码：应付列表上的「导出 Excel」按钮要它，后端导出接口校验同一个码。 */
export const FINANCE_PAYABLE_LIST_EXPORT_PERMISSION = "finance.payable_list:export";

/** 财务总览几张列表的每页条数（后端最多允许 50）。 */
export const FINANCE_OVERVIEW_PAGE_SIZE = 20;

/** 财务总览几张列表查询的 queryKey 根。 */
export const financeOverviewQueryKey = ["finance", "overview"] as const;

/** 一项文字筛选：去掉首尾空白后填了的才带上。 */
function filledParam(key: string, value: string | null): Record<string, string> {
  const text = value?.trim();
  return text ? { [key]: text } : {};
}

/**
 * 应收列表的筛选写成查询参数：接口（列表、导出）和地址共用这一份，参数名与接口一致
 * （`q`、`departure_from`、`departure_to`、`sales_name`），只带填了的、去掉首尾空白；勾了「还有尚欠」才带
 * `has_outstanding`（给接口是布尔值，写进地址是 `true`）。
 */
export function financeReceivableOverviewQuery(filters: FinanceReceivableOverviewFilters): Record<string, string | true> {
  return {
    ...filledParam("q", filters.keyword),
    ...filledParam("departure_from", filters.departureFrom),
    ...filledParam("departure_to", filters.departureTo),
    ...filledParam("sales_name", filters.salesName),
    ...(filters.hasOutstanding ? { has_outstanding: true as const } : {}),
  };
}

/**
 * 应付列表的筛选写成查询参数：接口（列表、导出）和地址共用这一份，参数名与接口一致（`q`、`departure_from`、
 * `departure_to`、`dispatcher_name`、`request_status`），只带填了的、去掉首尾空白；勾了「还有可请额度」才带
 * `has_requestable_amount`（给接口是布尔值，写进地址是 `true`）。
 */
export function financePayableOverviewQuery(filters: FinancePayableOverviewFilters): Record<string, string | true> {
  return {
    ...filledParam("q", filters.keyword),
    ...filledParam("departure_from", filters.departureFrom),
    ...filledParam("departure_to", filters.departureTo),
    ...filledParam("dispatcher_name", filters.dispatcherName),
    ...filledParam("request_status", filters.requestStatus),
    ...(filters.hasRequestableAmount ? { has_requestable_amount: true as const } : {}),
  };
}
